import {
	Anchor,
	Box,
	Center,
	Group,
	Paper,
	Text,
	useMantineTheme,
} from "@mantine/core";
import { useHash } from "@mantine/hooks";
import { SquareArrowOutUpRight, ZoomInIcon } from "lucide-react";
import type React from "react";
import { useContext, useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import { Zoom } from "yet-another-react-lightbox/plugins";
import { SiteContext } from "../../Contexts";
import getPhotoUrl from "../../inaturalist/photo-urls";
import UserAccount from "../user-account/UserAccount";
import { getMainImageHeight, outerWidth } from "./dimensions";
import classes from "./ObservationHero.module.css";
import ObservationPhoto from "./ObservationPhoto";
import ObservationPhotos from "./ObservationPhotos";

interface ObservationHeroProps {
	observation?: Observation;
}

const ObservationHero: React.FC<ObservationHeroProps> = ({ observation }) => {
	const [site] = useContext(SiteContext);
	const [photoIndex, setPhotoIndex] = useState(0);
	const [hash, setHash] = useHash();
	const theme = useMantineTheme();

	if (!observation) {
		return <Center>Loading...</Center>;
	}
	const { id, photos, user, taxon } = observation;

	const zoomHash = `#zoom${id}`;
	const lightboxOpen = hash === zoomHash;

	const url = new URL(site.url);
	url.pathname = `/observations/${id}`;

	const photo = photos[photoIndex];
	const imageHeight = getMainImageHeight(photos.length);

	const setLightBoxOpen = (open: boolean) => {
		setHash(open ? zoomHash : "");
	};

	return (
		<Paper w={outerWidth} radius="md" shadow="sm" withBorder>
			<Box style={{ position: "relative" }}>
				<ObservationPhoto photo={photo} h={imageHeight} />
				<Box className={classes.overlay} />
				<ZoomInIcon
					className={classes.zoomIcon}
					onClick={() => setLightBoxOpen(true)}
				/>
				<Box className={classes.taxonText}>
					{taxon && !taxon.preferred_common_name ? (
						<Text size="lg" fs="italic">
							{taxon?.name}
						</Text>
					) : (
						<>
							<Text size="lg">{taxon?.preferred_common_name ?? "Unknown"}</Text>
							<Text size="md" fs="italic" hidden={!taxon}>
								{` (${taxon?.name})`}
							</Text>
						</>
					)}
				</Box>
			</Box>
			<ObservationPhotos
				photos={photos}
				photoIndex={photoIndex}
				setPhotoIndex={setPhotoIndex}
			/>
			<Group align="center" p="0.4em" bg={theme.colors.gray[1]}>
				<UserAccount user={user} size="sm" />
				<Anchor
					ml="auto"
					h={24}
					href={url.toString()}
					target="_blank"
					rel="noreferrer"
					aria-label={`See it on ${site.name}`}
				>
					<SquareArrowOutUpRight />
				</Anchor>
			</Group>
			<Lightbox
				plugins={[Zoom]}
				open={lightboxOpen}
				close={() => setLightBoxOpen(false)}
				index={photoIndex}
				on={{
					view: (i) => setPhotoIndex(i.index),
					exiting: () => window.history.back(),
				}}
				slides={photos.map((p) => {
					return { src: getPhotoUrl(p, "large") };
				})}
				carousel={{ finite: true }}
				controller={{ closeOnBackdropClick: true }}
			/>
		</Paper>
	);
};

export default ObservationHero;
